import styled from 'styled-components';

import type {
  ThemeType,
} from '@n3/css-base';

import type {
  AreaProps,
} from './types';

const getAreaBorderColor = ({
  $isDisabled,
  $hasError,
  $hasWarning,

  theme: {
    checkbox: { COLORS: colors },
  },
}: AreaProps & { theme: ThemeType }): string => {
  if ($isDisabled) {
    return colors.disabled[2];
  }

  if ($hasError) {
    return colors.error[2];
  }

  if ($hasWarning) {
    return colors.warning[2];
  }

  return colors.default[2];
};

const getAreaBorderColorHover = ({
  $isDisabled,

  theme: {
    checkbox: { COLORS: colors },
  },
}: AreaProps & { theme: ThemeType }): string => {
  if ($isDisabled) {
    return colors.disabled[2];
  }

  return colors.hover[2];
};

const getAreaBorderColorActive = ({
  $isDisabled,
  $hasError,
  $hasWarning,

  theme: {
    checkbox: { COLORS: colors },
  },
}: AreaProps & { theme: ThemeType }): string => {
  if ($isDisabled) {
    return colors.disabled[2];
  }

  if ($hasError) {
    return colors.error[2];
  }

  if ($hasWarning) {
    return colors.warning[2];
  }

  return colors.active[2];
};

const getAreaBackgroundColor = ({
  $isDisabled,
  $isChecked,
  $isIndeterminate,
  $hasError,
  $hasWarning,

  theme: {
    checkbox: { COLORS: colors },
  },
}: AreaProps & { theme: ThemeType }): string => {
  if ($isIndeterminate || $isChecked) {
    if ($isDisabled) {
      return colors.disabled[0];
    }

    if ($hasError) {
      return colors.error[0];
    }

    if ($hasWarning) {
      return colors.warning[0];
    }

    return colors.checked[0];
  }

  return colors.default[0];
};

const getAreaBackgroundColorHover = ({
  $isDisabled,
  $isChecked,
  $isIndeterminate,

  theme: {
    checkbox: { COLORS: colors },
  },
}: AreaProps & { theme: ThemeType }): string => {
  if ($isIndeterminate || $isChecked) {
    if ($isDisabled) {
      return colors.disabled[0];
    }

    return colors.hover[0];
  }

  return colors.default[0];
};

const getAreaBackgroundColorActive = ({
  $isDisabled,
  $isChecked,
  $isIndeterminate,
  $hasError,
  $hasWarning,

  theme: {
    checkbox: { COLORS: colors },
  },
}: AreaProps & { theme: ThemeType }): string => {
  if ($isIndeterminate || $isChecked) {
    if ($isDisabled) {
      return colors.disabled[0];
    }

    if ($hasError) {
      return colors.error[0];
    }

    if ($hasWarning) {
      return colors.warning[0];
    }

    return colors.active[0];
  }

  return colors.default[0];
};

export const StyledInput = styled.input({
  opacity: 0,
  visibility: 'hidden',
  position: 'absolute',
  left: -9999,
});

export const StyledArea = styled.div<AreaProps>((props) => {
  const {
    theme: {
      checkbox,
    },
  } = props;

  return {
    boxSizing: 'border-box',
    verticalAlign: 'text-bottom',
    position: 'absolute',
    left: 0,
    top: checkbox.BOX_OFFSET_TOP,
    width: checkbox.WIDTH,
    height: checkbox.WIDTH,
    borderRadius: checkbox.BORDER_RADIUS,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: getAreaBorderColor(props),
    backgroundColor: getAreaBackgroundColor(props),
    textAlign: 'center',
  };
});

export const StyledCheckbox = styled.label<AreaProps>((props) => {
  const {
    $isDisabled,

    theme: {
      checkbox,

    },
  } = props;

  const { COLORS: colors } = checkbox;

  return {
    display: 'inline-block',
    position: 'relative',
    paddingLeft: checkbox.WIDTH + checkbox.TEXT_OFFSET,
    cursor: $isDisabled ? 'default' : 'pointer',
    color: $isDisabled ? colors.disabled[1] : colors.default[1],

    '&:hover': $isDisabled ? undefined : {
      color: colors.hover[1],

      [StyledArea]: {
        borderColor: getAreaBorderColorHover(props),
        backgroundColor: getAreaBackgroundColorHover(props),
      },
    },

    '&:active': $isDisabled ? undefined : {
      color: colors.active[1],

      [StyledArea]: {
        borderColor: getAreaBorderColorActive(props),
        backgroundColor: getAreaBackgroundColorActive(props),
      },
    },
  };
});

export const StyledIcon = styled.svg(({
  theme: {
    colors,
  },
}) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  fill: colors.NEUTRAL_BACKGROUND,
  transform: 'translateX(-50%) translateY(-50%)',
}));

export const StyledContent = styled.div({
  display: 'inline-block',
});

export const StyledTooltipCheckboxWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  columnGap: '8px',
});
